import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2 } from "lucide-react";
import { Wordmark } from "../components/Nav";
import { Honeypot, useGuard, submitForm } from "../components/forms";

const ROLES = [
  { v: "practicing_chaplain", l: "A practicing chaplain" },
  { v: "pastor_elder_exploring", l: "A pastor or church elder exploring chaplaincy" },
  { v: "other_interested", l: "Another spiritual care professional or interested person" },
];
const INTERESTS = [
  { v: "renewal_gathering", l: "Attending a renewal gathering at sea" },
  { v: "learn_chaplaincy", l: "Learning more about chaplaincy" },
  { v: "both", l: "Both" },
];

const SAILINGS = [
  { v: "renewal", l: "Chaplain Renewal at Sea" },
  { v: "exploring", l: "Exploring Chaplaincy at Sea" },
  { v: "remembrance", l: "From Diagnosis to Goodbye: Grief and Remembrance at Sea" },
  { v: "not_sure", l: "Not sure yet" },
];

const EMPTY = { name: "", email: "", location: "", role: "", interest: "", sailing: "", comments: "", consent_contact: false };

export default function InterestFormPage() {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const guard = useGuard();

  useEffect(() => {
    document.title = "Chaplain at Sea — Interest Form";
    return () => {
      document.title = "Chaplain at Sea | Rest. Renew. Write. Connect.";
    };
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name.trim() || !form.email.trim() || !form.location.trim() || !form.role || !form.interest || !form.sailing) {
      setError("Please complete all required fields.");
      return;
    }
    if (!form.consent_contact) {
      setError("Please check the consent box so we can contact you about this idea.");
      return;
    }
    setBusy(true);
    const res = await submitForm("/interest/quick", {
      ...form,
      started_at: guard.startedAt,
      website: guard.honeypot,
    });
    setBusy(false);
    if (res.ok) setDone(true);
    else setError(res.error);
  };

  return (
    <div className="flex min-h-screen flex-col bg-cream" data-testid="interest-form-page">
      <header className="border-b border-navy/10 bg-cream/95 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center justify-between px-5 py-4 sm:px-8">
          <Wordmark tone="light" />
          <Link to="/" data-testid="interest-form-home-link" className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.16em] text-navy/60 transition-colors hover:text-gold">
            <ArrowLeft size={13} strokeWidth={1.5} />
            Home
          </Link>
        </div>
      </header>

      <main className="mx-auto w-full max-w-2xl flex-1 px-5 py-12 sm:px-8 sm:py-16">
        <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold">Chaplain at Sea — Interest Form</p>
        <h1 className="mt-4 font-serif text-4xl font-light leading-tight text-navy sm:text-5xl" data-testid="interest-form-title">
          Add your name to the idea.
        </h1>
        <p className="mt-4 text-base leading-relaxed text-navy/70">
          Tell us a little about yourself and what draws you. Submitting this form is not a
          booking or commitment&mdash;it only shares your interest as plans develop.
        </p>

        {done ? (
          <div className="mt-10 border border-gold/50 bg-parchment p-8" data-testid="quick-success">
            <CheckCircle2 size={30} strokeWidth={1.25} className="text-gold" />
            <p className="mt-5 font-serif text-2xl font-light text-navy">Thank you, {form.name.split(" ")[0]}.</p>
            <p className="mt-3 text-sm leading-relaxed text-navy/70">
              Your interest has been received. We&rsquo;ll be in touch as plans develop. Submitting
              this form is not a booking or commitment.
            </p>
          </div>
        ) : (
          <form onSubmit={onSubmit} noValidate className="mt-10 border border-navy/12 bg-parchment p-6 sm:p-9" data-testid="quick-form">
            <Honeypot value={guard.honeypot} onChange={guard.setHoneypot} />
            <fieldset>
              <legend className="field-label text-navy/70">I am *</legend>
              <div className="mt-3 grid gap-2.5">
                {ROLES.map((r) => (
                  <label key={r.v} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-navy/85">
                    <input type="radio" name="role" value={r.v} checked={form.role === r.v} onChange={() => setForm((f) => ({ ...f, role: r.v }))} data-testid={"quick-role-" + r.v} className="mt-0.5 h-4 w-4 shrink-0 accent-[#B89047]" />
                    <span>{r.l}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-6">
              <legend className="field-label text-navy/70">I'm interested in *</legend>
              <div className="mt-3 grid gap-2.5">
                {INTERESTS.map((r) => (
                  <label key={r.v} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-navy/85">
                    <input type="radio" name="interest" value={r.v} checked={form.interest === r.v} onChange={() => setForm((f) => ({ ...f, interest: r.v }))} data-testid={"quick-interest-" + r.v} className="mt-0.5 h-4 w-4 shrink-0 accent-[#B89047]" />
                    <span>{r.l}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset className="mt-6">
              <legend className="field-label text-navy/70">Which sailing interests you? *</legend>
              <div className="mt-3 grid gap-2.5">
                {SAILINGS.map((r) => (
                  <label key={r.v} className="flex cursor-pointer items-start gap-3 text-sm leading-relaxed text-navy/85">
                    <input type="radio" name="sailing" value={r.v} checked={form.sailing === r.v} onChange={() => setForm((f) => ({ ...f, sailing: r.v }))} data-testid={"quick-sailing-" + r.v} className="mt-0.5 h-4 w-4 shrink-0 accent-[#B89047]" />
                    <span>{r.l}</span>
                  </label>
                ))}
              </div>
              <p className="mt-3 text-xs leading-relaxed text-navy/55" data-testid="quick-sailing-note">
                Each sailing is its own dedicated cruise with a light, theme-specific program across
                3-, 4-, or 5-day itineraries. Dates and registration will be announced when
                confirmed.
              </p>
            </fieldset>

            <div className="mt-6 grid gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="q-name" className="field-label text-navy/70">Name *</label>
                <input id="q-name" required data-testid="quick-name-input" className="field-input mt-1.5" value={form.name} onChange={set("name")} />
              </div>
              <div>
                <label htmlFor="q-email" className="field-label text-navy/70">Email *</label>
                <input id="q-email" type="email" required data-testid="quick-email-input" className="field-input mt-1.5" value={form.email} onChange={set("email")} />
              </div>
            </div>
            <div className="mt-5">
              <label htmlFor="q-location" className="field-label text-navy/70">General location *</label>
              <input id="q-location" required data-testid="quick-location-input" className="field-input mt-1.5" value={form.location} onChange={set("location")} placeholder="City, state, or region" />
            </div>
            <div className="mt-5">
              <label htmlFor="q-comments" className="field-label text-navy/70">Comments (optional)</label>
              <textarea id="q-comments" rows={3} data-testid="quick-comments-input" className="field-input mt-1.5 resize-y" value={form.comments} onChange={set("comments")} placeholder="Anything you would like us to know" />
            </div>

            <label className="mt-6 flex items-start gap-3 text-sm leading-relaxed text-navy/85">
              <input type="checkbox" required checked={form.consent_contact} onChange={set("consent_contact")} data-testid="quick-consent-checkbox" className="mt-1 h-4 w-4 shrink-0 accent-[#B89047]" />
              <span>I agree to be contacted about this idea and future Chaplain at Sea plans.</span>
            </label>
            <p className="mt-4 text-xs leading-relaxed text-navy/55" data-testid="quick-disclaimer">
              Submitting this form is not a booking or commitment. Your information is used only to
              share updates and is never sold.
            </p>
            {error && (
              <p role="alert" data-testid="quick-error" className="mt-5 border border-red-800/30 bg-red-50 px-4 py-3 text-sm text-red-900">
                {error}
              </p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="quick-submit-btn"
              className="mt-7 inline-flex w-full items-center justify-center bg-gold px-8 py-4 font-mono text-[12px] uppercase tracking-[0.2em] text-navy transition-all duration-300 hover:bg-gold-light disabled:opacity-60 sm:w-auto"
            >
              {busy ? "Sending..." : "Share My Interest"}
            </button>
          </form>
        )}
      </main>

      <footer className="border-t border-navy/10 py-8">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <p className="text-xs leading-relaxed text-navy/50">
            © {new Date().getFullYear()} Chaplain at Sea. Rest. Renew. Write. Connect. Our
            inaugural retreat is in development; no payments or reservations are accepted at this
            time.
          </p>
        </div>
      </footer>
    </div>
  );
}
